import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import environment from '../../../environments/environment';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../services/auth/jwt-legfi-claims.model';
import { MembersService } from '../../services/members/members.service';
import { TreasuryDisplayStatus, TreasuryService } from '../../services/stripe-treasury/treasury.service';
import { ONBOARDING_STATUSES } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { TwoFactorGateDialogComponent } from '../../components/v2-layout/shared/two-factor-gate-dialog/two-factor-gate-dialog.component';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';

/** Security page where either audience finishes 2FA enrolment. */
const SECURITY_ROUTE = '/app/settings/user/security';
/** Sign-in route for the Treasury hard-gate "Back to login" escape. */
const LOGIN_ROUTE = '/auth/login';

/**
 * Mandatory-2FA access guard for two distinct audiences:
 *
 * 1. **Super users (SD-5787).** PayHOA super-admin staff must have 2FA enrolled before reaching the
 *    super-admin panel. An un-enrolled super user is redirected to the Security page to set it up.
 *
 * 2. **Treasury required users (SD-8025 enforcement / SD-8102 UX).** Decided server-side via the
 *    `mfaRequired` JWT claim (financial-account write-access users and spend cardholders; super
 *    users are exempt from this claim, so the two audiences never overlap). A required user is
 *    login-gated only once their org has a **verified** Stripe Connect account — pre-Treasury orgs
 *    enrol just-in-time at KYC instead — and is held on the shared `disableClose` gate dialog
 *    (`TREASURY_LOGIN`) until they enrol. The server middleware remains the authoritative
 *    money-movement gate; this guard is the front-door UX.
 *
 * Production-only, matching the server enforcement gate.
 */
@Injectable({
    providedIn: 'root',
})
export class SuperUser2faGuard implements CanActivate, CanActivateChild
{
    private _twoFactorEnabled: boolean | null = null;

    constructor(
            private _router: Router,
            private _membersService: MembersService,
            private _treasuryService: TreasuryService,
            private _dialog: MatDialog,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        if (!environment.production) {
            return true;
        }

        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return true;
        }

        // Already confirmed enrolled this session — no need to re-gate either audience.
        if (this._twoFactorEnabled === true) {
            return true;
        }

        // Always allow the Security page so either audience can finish enrolment there.
        if (state.url.startsWith(SECURITY_ROUTE)) {
            return true;
        }

        // Super-user mandatory 2FA (SD-5787): enforce enrolment before super-admin access.
        if (jwt.superUser) {
            return this._enforceSuperUser2fa(jwt);
        }

        // Treasury mandatory 2FA (SD-8025 / SD-8102): gate required users in verified-Connect orgs.
        if (jwt.mfaRequired) {
            return this._enforceTreasury2fa(jwt);
        }

        return true;
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        return this.canActivate(childRoute, state);
    }

    clearCache(): void {
        this._twoFactorEnabled = null;
    }

    /**
     * Super-user path (SD-5787): an un-enrolled super user is redirected to the Security page to set
     * up 2FA before continuing. A cross-org super session with no memberId can't be read, so it's
     * allowed through (matches the original control).
     */
    private _enforceSuperUser2fa(jwt: JwtLegFiClaims): Observable<boolean> {
        if (!jwt.memberId) {
            return of(true);
        }

        return this._membersService.getMember(jwt.orgId, jwt.memberId).pipe(
                map((membership) => {
                    const enrolled = membership.user.isGoogleAuthVerificationEnrolled
                            || membership.user.isSmsAuthVerificationEnrolled;
                    this._twoFactorEnabled = enrolled;

                    if (enrolled) {
                        return true;
                    }

                    void this._router.navigate([SECURITY_ROUTE]);
                    return false;
                }),
                catchError(() => of(true)),
        );
    }

    /**
     * Treasury path (SD-8025 / SD-8102): only verified-Treasury orgs are login-gated; pre-Treasury
     * orgs enrol just-in-time at KYC, so wait for the Connect status before deciding. An un-enrolled
     * required user is held on the shared hard-gate dialog until enrolment completes.
     */
    private _enforceTreasury2fa(jwt: JwtLegFiClaims): Observable<boolean> {
        const orgId = jwt.orgId;
        const memberId = jwt.memberId;
        if (!memberId) {
            // A cross-org session has no memberId, so the live member-2FA read cannot run.
            return of(true);
        }

        this._treasuryService.prefetchConnectStatus(orgId);

        return this._treasuryService.connectStatus$.pipe(
                filter((status): status is TreasuryDisplayStatus => status !== null),
                take(1),
                switchMap((status) => {
                    if (status !== ONBOARDING_STATUSES.VERIFIED) {
                        return of(true);
                    }

                    return this._membersService.getMember(orgId, memberId).pipe(
                            switchMap((membership) => {
                                const enrolled = membership.user.isGoogleAuthVerificationEnrolled
                                        || membership.user.isSmsAuthVerificationEnrolled;
                                this._twoFactorEnabled = enrolled;

                                if (enrolled) {
                                    return of(true);
                                }

                                // Hard login gate: open the enrolment dialog and block
                                // navigation until it resolves. TREASURY_LOGIN is a
                                // disableClose gate, so it only resolves `true` once the
                                // user has enrolled.
                                return TwoFactorGateDialogComponent
                                        .open(this._dialog, TwoFactorGateReason.TREASURY_LOGIN)
                                        .afterClosed()
                                        .pipe(
                                                map((result) => {
                                                    const nowEnrolled = result === true;
                                                    this._twoFactorEnabled = nowEnrolled;

                                                    if (!nowEnrolled) {
                                                        // User cancelled the hard gate — clear the login
                                                        // attempt and return to sign-in (an escape hatch,
                                                        // no self-service 2FA recovery yet, SD-8082).
                                                        this._backToLogin();
                                                    }

                                                    return nowEnrolled;
                                                }),
                                        );
                            }),
                            catchError(() => of(true)),
                    );
                }),
                catchError(() => of(true)),
        );
    }

    /**
     * Clear the login attempt and hard-redirect to sign-in. A full-page redirect
     * (not `Router.navigate`) is used deliberately: navigating via the router while
     * this guard is still resolving re-enters the router, and the JWT is cleared
     * statically so the guard needn't depend on AuthService — both were the cause of
     * the earlier failure.
     */
    private _backToLogin(): void {
        LegFiJwtService.clear();
        window.location.href = LOGIN_ROUTE;
    }
}
